'use client'

/**
 * Shared email-body preview — the single canonical renderer for every
 * SafeAttack surface that is NOT the live training player.
 *
 * Why it exists: before this component, 5 render sites (library detail,
 * library grid card, admin submissions x2, admin generate) each rolled
 * their own `prose prose-sm` + `dangerouslySetInnerHTML`. Two of them
 * wrapped `prose` inside a `space-y-3` flex parent, which collapsed the
 * visual gap between paragraphs. This component guarantees consistent
 * spacing via the `.pt-email-body` CSS block in globals.css.
 *
 * NOT used by the live player (InteractiveEmail.tsx) because the player
 * needs per-element React refs for phish-marker highlighting, which
 * `dangerouslySetInnerHTML` cannot support.
 *
 * Links in the attack are INERT by default (P2, 2026-10-06): they point at
 * whatever the attack names, often a domain the AI invented that anyone may
 * own, and one click left the product. Hovering still shows the destination.
 * See lib/email-preview/use-inert-links.ts.
 */

import { resolveBrandImages } from '@/lib/phishtrainer/engines/brand-logos'
import { resolveSamplePlaceholders } from '@/lib/phishtrainer/sample-placeholders'
import { stripDuplicateGreeting } from '@/lib/phishtrainer/email-formatter'
import { sanitizeHtmlFragment } from '@/lib/sanitize/sanitize-html-client'
import { useInertLinks } from '@/lib/email-preview/use-inert-links'

interface EmailBodyPreviewProps {
  greeting?: string
  bodyHtml?: string
  className?: string
  brandHint?: string
}

export function EmailBodyPreview({
  greeting,
  bodyHtml,
  className,
  brandHint,
}: EmailBodyPreviewProps) {
  const inertLinks = useInertLinks()

  if (!bodyHtml && !greeting) {
    return (
      <div className="rounded-md border border-dashed p-8 text-sm text-muted-foreground">
        Email body could not be parsed from the original source.
      </div>
    )
  }

  // Fill recipient/company merge fields with a sample identity so the preview
  // doesn't leak raw template code like {{ .Recipient.FirstName }} or
  // [Recipient Company Name] (#1000, #1044).
  const resolvedGreeting = resolveSamplePlaceholders(greeting)
  // Drop a greeting the body repeats: the greeting is rendered from its own
  // field below, so a body that ALSO opens with it printed it twice ("Hi
  // Sarah," / "Hi Sarah," / "As discussed…"). The play adapter already strips
  // this, which is why the game looked right while every preview surface (this
  // library card, the detail sheet, the player round-detail, the AI-gen result
  // panels) showed the duplicate.
  const dedupedBody = stripDuplicateGreeting(bodyHtml ?? '', greeting)
  // Same backend the rest of the app talks to, so a `data-brand-key` that has no
  // inline SVG still loads its logo PNG from our own origin (mirrors the
  // ai-studio resolver's base source). NEXT_PUBLIC_* is inlined at build.
  const backendBaseUrl = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4001'
  // SafeAttack bodyHtml is authored/AI-generated content stored verbatim by the
  // BE. It reaches this shared preview across 5 admin surfaces; sanitize before
  // injecting so a crafted attack body (<img onerror>, <iframe srcdoc>, on*) can
  // never run script in a reviewing admin's session (stored XSS). DOMPurify keeps
  // structure + inline styles, drops the executable bits.
  const resolvedBody = dedupedBody
    ? sanitizeHtmlFragment(
        resolveBrandImages(
          resolveSamplePlaceholders(dedupedBody),
          brandHint,
          backendBaseUrl,
        ),
      )
    : ''

  return (
    <div className={className}>
      {/* One white email-client canvas around greeting + body. Raw SafeAttack
          HTML carries inline colors authored for white; rendering it on the
          app's themed surface made dark-text emails vanish in dark mode and
          light-text emails vanish in light mode. `.hf-email-preview` pins it to
          a real inbox canvas (white bg + dark text, isolated from the palette)
          so every attack is legible in BOTH themes. */}
      <div className="hf-email-preview">
        {greeting && <p className="hf-email-greeting">{resolvedGreeting}</p>}
        {bodyHtml && (
          <div
            className="pt-email-body"
            {...inertLinks}
            dangerouslySetInnerHTML={{ __html: resolvedBody }}
          />
        )}
      </div>
    </div>
  )
}
